import debounce from 'lodash-es/debounce'
import isNil from 'lodash-es/isNil'
import { RefObject, useCallback, useLayoutEffect, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'

function getSize<E extends HTMLElement>(el?: E) {
  if (!el) {
    return {
      width: undefined,
      height: undefined
    }
  }

  return {
    width: el.offsetWidth,
    height: el.offsetHeight
  }
}

function useComponentSize<E extends HTMLElement>(ref: RefObject<E | null>, debounceInterval = 0) {
  const [componentSize, setComponentSize] = useState(
    getSize(!isNil(ref) && !isNil(ref.current) ? ref.current : undefined)
  )

  const handleResize = useCallback(() => {
    if (!isNil(ref) && !isNil(ref.current)) {
      setComponentSize(getSize(ref.current))
    }
  }, [ref])

  const debouncedHandleResize = debounce(handleResize, debounceInterval)

  useLayoutEffect(
    () => {
      if (!isNil(ref) && !isNil(ref.current)) {
        debouncedHandleResize()

        const resizeObserver = new ResizeObserver(() => {
          debouncedHandleResize()
        })
        resizeObserver.observe(ref?.current)

        return () => {
          resizeObserver.disconnect()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return componentSize
}

export default useComponentSize
